import { useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/router';

import {
  fromQueryString,
  getQueryString,
  isDate,
  isEmpty
} from 'groundkeeper-component-library';
import { isClient } from 'shared/Redux/utils';

export const Query = {
  toObject: query => {
    const queryObject = {};
    const objectify = value => {
      if (Number.isNaN(value) && isDate(new Date(value))) {
        return new Date(value);
      }

      return value;
    };

    if (query) {
      Object.keys(query).forEach(key => {
        let value = query[key];

        if (Array.isArray(value)) {
          value = value.map(v => objectify(v));
        } else if (
          (typeof value === 'string' || value instanceof String) &&
          value.split(';').length > 1
        ) {
          value = value.split(';');
        } else if (isDate(fromQueryString(value))) {
          value = fromQueryString(value);
        } else if (value === 'true') {
          value = true;
        } else if (value === 'false') {
          value = false;
        } else {
          value = objectify(value);
        }

        queryObject[key] = value;
      });
    }

    return queryObject;
  },
  toQuery: object => {
    const queryObject = {};
    const queryfy = value => {
      if (isDate(value)) {
        return getQueryString(value);
      }

      return value;
    };

    if (object) {
      Object.keys(object).forEach(key => {
        let value = object[key];

        if (Array.isArray(value)) {
          value = value.map(v => queryfy(v)).join(';');
        } else {
          value = queryfy(value);
        }

        queryObject[key] = value;
      });
    }

    return queryObject;
  }
};

export default function useFilter<FilterType>(initialFilter) {
  const router = useRouter();
  const [filter, setFilter] = useState<FilterType>({
    ...Query.toObject(router.query),
    ...Query.toObject(initialFilter)
  } as FilterType);
  const [loading, setLoading] = useState(false);
  const batchTimeout = useRef(null);

  useEffect(() => {
    setFilter({
      ...Query.toObject(router.query),
      ...Query.toObject(initialFilter)
    } as FilterType);
  }, [initialFilter]);

  useEffect(() => {
    function routeChangeComplete() {
      setLoading(false);
    }

    router.events.on('routeChangeComplete', routeChangeComplete);
    router.events.on('routeChangeError', routeChangeComplete);
    return () => {
      router.events.off('routeChangeComplete', routeChangeComplete);
      router.events.off('routeChangeError', routeChangeComplete);
    };
  }, []);

  async function handleSortChange(key) {
    setLoading(true);
    const query = {
      ...router.query
    };
    ![`${key}_desc`, key].includes;
    if (!query.order_by || ![`${key}_desc`, key].includes(query.order_by)) {
      query.order_by = key;
    } else if (query.order_by === key) {
      query.order_by = `${key}_desc`;
    } else if (query.order_by === `${key}_desc`) {
      delete query.order_by;
    }

    // If there's no query param left we have to
    // push the pathname, an empty object doesn't
    // trigger a navigation.
    if (Object.keys(query).length > 0) {
      router.push({
        query
      });
    } else {
      router.push(router.pathname);
    }
  }

  function handleFilterChange(values, options = { shallow: false }) {
    const query = {
      ...router.query,
      ...Query.toQuery(values)
    };
    // Remove undefined and null entries to reset filter
    Object.keys(query).forEach(
      key =>
        (query[key] === undefined || query[key] === null || key === 'page') &&
        delete query[key]
    );

    const diff = JSON.stringify(router.query) !== JSON.stringify(query);
    if (diff) {
      setLoading(true);
      setFilter(Query.toObject(query) as FilterType);

      clearTimeout(batchTimeout.current);
      batchTimeout.current = setTimeout(() => {
        // To reset query we have to use the pathname. query = {} does not reset query
        if (isClient) {
          if (isEmpty(query)) {
            router.push(router.pathname, undefined, {
              scroll: false,
              shallow: options.shallow
            });
          } else {
            router.push({ query }, undefined, {
              scroll: false,
              shallow: options.shallow
            });
          }
        }
      }, 100);
    }
  }

  return { filter, handleFilterChange, handleSortChange, loading };
}
